import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  runTransaction,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { createLogger } from '../../utils/logger';
import { CART_STATUS, StatusHelpers } from '../../constants';
import { generateWipayOrderId } from './wipayOrderId';
import { buildCheckoutData, buildWipayGatewayData } from './buildCheckoutData';
import { buildWipayRequestParams, requestWipayPaymentUrl } from './buildWipayRequest';
import { shouldReplacePendingOrderId } from './checkoutOrderGuard';
import { decrementInventory } from './paymentInventory';

const logger = createLogger('checkoutPayment');

/**
 * BUG: Stock check is a separate non-transactional read-modify path. Concurrent
 * checkouts can both pass the check and oversell; prefer server-side atomic claims.
 */
export async function checkCartStock(supermarketId, products) {
  const insufficientStock = [];

  for (const product of products) {
    const inventoryQuery = query(
      collection(db, 'Inventory'),
      where('supermarket_id', '==', supermarketId),
      where('product_id', '==', product.productId),
      limit(1)
    );
    const snapshot = await getDocs(inventoryQuery);

    if (!snapshot.empty) {
      const data = snapshot.docs[0].data();
      const availableQty = data.quantity || 0;
      if ((product.quantity || 0) > availableQty) {
        insufficientStock.push({
          name: product.name || 'Product',
          requested: product.quantity,
          available: availableQty,
        });
      }
    } else {
      insufficientStock.push({
        name: product.name || 'Product',
        requested: product.quantity,
        available: 0,
      });
    }
  }

  return insufficientStock;
}

async function resolveUserIdentityId(currentUser) {
  let userIdentityId = currentUser.uid;
  try {
    const userDocSnap = await getDoc(doc(db, 'Users', currentUser.uid));
    if (userDocSnap.exists()) {
      userIdentityId = userDocSnap.data().identity_id || currentUser.uid;
      logger.debug('[checkoutPayment] Found user identity_id:', userIdentityId);
    } else {
      logger.warn(
        '[checkoutPayment] User document not found for ID:',
        currentUser.uid,
        '- using auth UID as fallback'
      );
    }
  } catch (error) {
    logger.error('[checkoutPayment] Error fetching user identity_id:', error);
  }
  return userIdentityId;
}

function buildInsufficientStockMessage(insufficientStock) {
  let msg = 'Some items in your cart exceed available inventory:\n\n';
  insufficientStock.forEach((item) => {
    msg += `- ${item.name}: Only ${item.available} left in stock (you requested ${item.requested})\n`;
  });
  msg += '\nPlease adjust your cart quantities before proceeding.';
  return msg;
}

async function persistSimulatedSuccessCart({
  currentUser,
  supermarketId,
  cartInstanceId,
  generatedOrderId,
  checkoutSnapshot,
}) {
  const cartRef = doc(db, 'Carts', currentUser.uid);
  await runTransaction(db, async (transaction) => {
    const cartDoc = await transaction.get(cartRef);
    if (!cartDoc.exists()) return;

    const cartData = cartDoc.data();
    const supermarketCartInstances = cartData[supermarketId] || {};
    if (!supermarketCartInstances[cartInstanceId]) return;

    const currentStatus = supermarketCartInstances[cartInstanceId].status;
    logger.debug('[checkoutPayment] Current cart status before simulated payment:', currentStatus);

    if (StatusHelpers.canUpdateToAwaitingPacking(currentStatus)) {
      supermarketCartInstances[cartInstanceId].status = CART_STATUS.AWAITING_PACKING;
      supermarketCartInstances[cartInstanceId].orderid = generatedOrderId;
      supermarketCartInstances[cartInstanceId].checkoutSnapshot = checkoutSnapshot;
      supermarketCartInstances[cartInstanceId].lastUpdatedAt = serverTimestamp();
      transaction.update(cartRef, {
        [supermarketId]: supermarketCartInstances,
        lastGlobalUpdate: serverTimestamp(),
      });
    } else if (currentStatus === CART_STATUS.AWAITING_PACKING) {
      if (!supermarketCartInstances[cartInstanceId].orderid) {
        supermarketCartInstances[cartInstanceId].orderid = generatedOrderId;
        supermarketCartInstances[cartInstanceId].checkoutSnapshot = checkoutSnapshot;
        supermarketCartInstances[cartInstanceId].lastUpdatedAt = serverTimestamp();
        transaction.update(cartRef, {
          [supermarketId]: supermarketCartInstances,
          lastGlobalUpdate: serverTimestamp(),
        });
      }
    } else {
      logger.warn(
        '[checkoutPayment] Cart is in more advanced status:',
        currentStatus,
        '- cannot update to awaiting_packing'
      );
    }
  });
}

async function persistWipayOrderOnCart({
  currentUser,
  supermarketId,
  cartInstanceId,
  wipayOrderId,
  checkoutSnapshot,
}) {
  const cartRef = doc(db, 'Carts', currentUser.uid);
  await runTransaction(db, async (transaction) => {
    const cartDoc = await transaction.get(cartRef);
    if (!cartDoc.exists()) return;

    const cartData = cartDoc.data();
    const supermarketCartInstances = cartData[supermarketId] || {};
    if (!supermarketCartInstances[cartInstanceId]) return;

    const currentStatus = supermarketCartInstances[cartInstanceId].status;
    logger.debug('[checkoutPayment] Current cart status before WiPay redirect:', currentStatus);

    if (StatusHelpers.isInvalidForPayment(currentStatus)) {
      logger.warn(
        '[checkoutPayment] Cart is in invalid status for payment:',
        currentStatus,
        '- cannot proceed to WiPay'
      );
      throw new Error(
        `Cart is in invalid status: ${StatusHelpers.getDisplayText(currentStatus)}`
      );
    }

    const existingOrderId = supermarketCartInstances[cartInstanceId].orderid;
    if (
      shouldReplacePendingOrderId({
        existingOrderId,
        currentStatus,
        wipayOrderId,
      })
    ) {
      logger.debug(
        '[checkoutPayment] Replacing previous WiPay orderid on cart before redirect:',
        existingOrderId,
        '->',
        wipayOrderId
      );
      const previousSnapshot = supermarketCartInstances[cartInstanceId].checkoutSnapshot;
      if (previousSnapshot && existingOrderId) {
        supermarketCartInstances[cartInstanceId].pendingCheckoutSnapshots = {
          ...(supermarketCartInstances[cartInstanceId].pendingCheckoutSnapshots || {}),
          [existingOrderId]: previousSnapshot,
        };
      }
    }

    supermarketCartInstances[cartInstanceId].orderid = wipayOrderId;
    supermarketCartInstances[cartInstanceId].checkoutSnapshot = checkoutSnapshot;
    supermarketCartInstances[cartInstanceId].lastUpdatedAt = serverTimestamp();
    transaction.update(cartRef, {
      [supermarketId]: supermarketCartInstances,
      lastGlobalUpdate: serverTimestamp(),
    });
  });
}

/**
 * Orchestrate checkout payment (simulated or WiPay).
 *
 * @returns {Promise<{ status: 'validation_error'|'stock_error'|'scheduled'|'redirecting'|'failed', message?: string, notification?: object }>}
 */
export async function startCheckoutPayment({
  currentUser,
  cartInstance,
  supermarketId,
  cartInstanceId,
  supermarket,
  confirmationFee,
  gatewayPaymentType,
  paymentType,
  orderSubtotal,
  applicableDeliveryFee,
  configuredMaxDeliveryDistance,
  gctSummary,
  deliveryOption,
  deliveryAddress,
  deliveryPhone,
  validateDeliveryForPayment,
  persistDeliveryIfNeeded,
  hasConfiguredDeliveryFee,
  wipayAccountNumber,
  wipayEnvironment,
  wipayFeeStructure,
  wipayResponseUrl,
  wipayCountryCode,
  wipayCardType,
  simulatedPaymentFails,
  navigate,
  /**
   * Optional schedule helper for simulated delay. Defaults to setTimeout.
   * Caller should clear the handle on unmount and ignore callbacks when aborted.
   */
  scheduleSimulatedPayment = (fn, delayMs) => setTimeout(fn, delayMs),
  redirectTo = (url) => {
    window.location.href = url;
  },
}) {
  if (!currentUser || !cartInstance || confirmationFee <= 0) {
    return {
      status: 'validation_error',
      notification: {
        type: 'error',
        title: 'Checkout Error',
        message: 'Cannot proceed: Missing user, cart instance, or fee is not set.',
      },
    };
  }

  const deliveryValidation = validateDeliveryForPayment({ hasConfiguredDeliveryFee });
  if (!deliveryValidation.ok) {
    return {
      status: 'validation_error',
      notification: {
        type: deliveryValidation.type,
        title: deliveryValidation.title,
        message: deliveryValidation.message,
      },
    };
  }

  try {
    await persistDeliveryIfNeeded();
  } catch (error) {
    logger.error('[checkoutPayment] Error saving delivery address:', error);
    return {
      status: 'validation_error',
      notification: {
        type: 'error',
        title: 'Delivery Error',
        message: 'Failed to save delivery details. Please try again.',
      },
    };
  }

  const userIdentityId = await resolveUserIdentityId(currentUser);

  // BUG: Uses in-memory cartInstance.products — another tab can mutate the cart
  // between click and redirect; the write transaction does not re-validate lines.
  const insufficientStock = await checkCartStock(supermarketId, cartInstance.products);
  if (insufficientStock.length > 0) {
    return {
      status: 'stock_error',
      notification: {
        type: 'error',
        title: 'Checkout Error',
        message: buildInsufficientStockMessage(insufficientStock),
      },
    };
  }

  const isFullPayment = paymentType === 'full' && supermarket?.full_payment_enabled;
  const paymentAmount = isFullPayment
    ? orderSubtotal + confirmationFee + applicableDeliveryFee
    : confirmationFee + applicableDeliveryFee;

  const deliveryCheckoutFields = {
    deliveryFee: applicableDeliveryFee,
    maxDeliveryDistance: applicableDeliveryFee > 0 ? configuredMaxDeliveryDistance : null,
  };

  const makeCheckoutPayload = (wipayOrderId) =>
    buildCheckoutData({
      userId: userIdentityId,
      cartDocId: currentUser.uid,
      supermarketId,
      cartInstanceId,
      supermarketName: cartInstance.supermarketName,
      cartItems: cartInstance.products,
      confirmationFee,
      orderSubtotal,
      isFullPayment,
      deliveryOption,
      deliveryAddress,
      deliveryPhone,
      deliveryCheckoutFields,
      gctSummary,
      wipayOrderId,
    });

  if (gatewayPaymentType === 'simulated') {
    const timeoutId = scheduleSimulatedPayment(async () => {
      const generatedOrderId = generateWipayOrderId(cartInstanceId);
      const checkoutPayload = makeCheckoutPayload(generatedOrderId);
      const checkoutSnapshot = JSON.parse(JSON.stringify(checkoutPayload));
      const compactData = encodeURIComponent(
        JSON.stringify(buildWipayGatewayData(checkoutPayload, { compact: true }))
      );

      if (simulatedPaymentFails) {
        navigate(
          `/payment/response?status=failed&order_id=${encodeURIComponent(generatedOrderId)}&data=${compactData}&delivery_option=${encodeURIComponent(deliveryOption)}&total=${encodeURIComponent(paymentAmount)}&message=Simulated payment failure`
        );
        return;
      }

      // BUG: Simulated path decrements inventory before a durable paid transaction
      // exists, with no inventoryDecremented claim/restore. Partial failure after
      // decrement leaves stock reduced without a consistent order. Real WiPay
      // decrements only on WipayResponsePage after success.
      await decrementInventory(supermarketId, cartInstance.products);

      await persistSimulatedSuccessCart({
        currentUser,
        supermarketId,
        cartInstanceId,
        generatedOrderId,
        checkoutSnapshot,
      });

      // Employee notify intentionally omitted here — WipayResponsePage handles it
      // (avoids duplicate alerts that previously fired from both pages).
      //
      // NOTE / BUG (callback page): WipayResponsePage treats status=success from the
      // query string without cryptographic hash / expected-total verification, and
      // may race auth hydration on return. Fixing that is out of scope for Checkout.
      logger.debug(
        '[checkoutPayment] Simulated payment - transaction record will be created by WipayResponsePage'
      );
      navigate(
        `/payment/response?status=success&order_id=${encodeURIComponent(generatedOrderId)}&data=${compactData}&delivery_option=${encodeURIComponent(deliveryOption)}&total=${encodeURIComponent(paymentAmount)}`
      );
    }, 1500);

    return { status: 'scheduled', timeoutId };
  }

  const wipayOrderId = generateWipayOrderId(cartInstanceId);
  const checkoutPayload = makeCheckoutPayload(wipayOrderId);
  const checkoutSnapshot = JSON.parse(JSON.stringify(checkoutPayload));
  const wipayData = buildWipayGatewayData(checkoutPayload, { compact: true });
  const parameters = buildWipayRequestParams({
    accountNumber: wipayAccountNumber,
    environment: wipayEnvironment,
    feeStructure: wipayFeeStructure,
    responseUrl: wipayResponseUrl,
    wipayOrderId,
    paymentAmount,
    wipayData,
    countryCode: wipayCountryCode,
    cardType: wipayCardType,
  });

  logger.debug(
    '[checkoutPayment] WiPay Order ID:',
    wipayOrderId,
    '(length:',
    wipayOrderId.length,
    ')'
  );

  try {
    const json = await requestWipayPaymentUrl(parameters);
    if (!json.url) {
      return {
        status: 'failed',
        notification: {
          type: 'error',
          title: 'Payment Failed',
          message: `Payment initiation failed: ${json.message || 'Unknown error'}`,
        },
      };
    }

    await persistWipayOrderOnCart({
      currentUser,
      supermarketId,
      cartInstanceId,
      wipayOrderId,
      checkoutSnapshot,
    });

    // Caller should recover if the browser blocks/stalls this navigation
    // (CheckoutPage uses a timeout + manual link).
    redirectTo(json.url);
    return { status: 'redirecting', redirectUrl: json.url };
  } catch (err) {
    return {
      status: 'failed',
      notification: {
        type: 'error',
        title: 'Payment Error',
        message: `Failed to initiate payment. ${err.message || err}`,
      },
    };
  }
}
